"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type ThemeMode = "light" | "dark" | "auto";

interface ThemeContextProps {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<ThemeMode>("auto");

  const getMediaTheme = useCallback(() => {
    const theme = window.matchMedia(`(prefers-color-scheme: dark)`).matches
      ? "dark"
      : "light";
    return theme as ThemeMode;
  }, []);

  const saveToLocalStorage = useCallback((newMode: ThemeMode) => {
    localStorage.setItem("flowbite-theme-mode", newMode);
    localStorage.setItem("theme", newMode);
  }, []);

  const setLight = useCallback(() => {
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("light");
    saveToLocalStorage("light");
  }, [saveToLocalStorage]);

  const setDark = useCallback(() => {
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
    saveToLocalStorage("dark");
  }, [saveToLocalStorage]);

  const setAuto = useCallback(() => {
    const mediaTheme = getMediaTheme();
    mediaTheme === "dark" ? setDark() : setLight();
    saveToLocalStorage("auto");
  }, [getMediaTheme, setDark, setLight, saveToLocalStorage]);

  const applyMode = useCallback(
    (newMode: ThemeMode) => {
      switch (newMode) {
        case "light":
          setLight();
          break;
        case "dark":
          setDark();
          break;
        case "auto":
          setAuto();
          break;
      }
    },
    [setDark, setLight, setAuto],
  );

  useEffect(() => {
    const savedMode = localStorage.getItem("flowbite-theme-mode") as ThemeMode;
    if (savedMode) {
      setMode(savedMode);
      applyMode(savedMode);
      return;
    }

    const mediaTheme = getMediaTheme();
    applyMode(mediaTheme);
  }, [getMediaTheme, applyMode]);

  const toggleMode = () => {
    const newMode: ThemeMode =
      mode === "light" ? "dark" : mode === "dark" ? "auto" : "light";
    setMode(newMode);
    localStorage.setItem("flowbite-theme-mode", newMode);
    localStorage.setItem("theme", newMode);
    applyMode(newMode);
  };

  return (
    <ThemeContext.Provider value={{ mode, setMode, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export { ThemeProvider, useTheme };
